"use client";

// Saved: a review surface for every bookmark and highlight the reader has
// created, aggregated across the whole library. There is no single "all
// annotations" backend endpoint, so this fetches the book list and then each
// book's bookmarks + highlights in parallel (reader/api.ts) and groups them by
// book. Each row deep-links back into the reader at its chunk via ?chunk=.

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Bookmark as BookmarkIcon, Loader2 } from "lucide-react";

import NavBar from "@/components/NavBar";
import { createLumiApiClient, LumiApiError } from "@/lib/lumi-api";
import { listBookmarks, listHighlights } from "@/components/lumi-rust/reader/api";
import type { Bookmark, Highlight } from "@/components/lumi-rust/reader/types";

type Tab = "bookmarks" | "highlights";

interface BookAnnotations {
  bookId: string;
  title: string;
  bookmarks: Bookmark[];
  highlights: Highlight[];
}

function locLabel(chunkIndex: number, charOffset?: number): string {
  const base = `Chunk ${chunkIndex + 1}`;
  return charOffset && charOffset > 0 ? `${base} · ${charOffset}` : base;
}

function readerHref(bookId: string, chunkIndex: number, offset?: number): string {
  const params = new URLSearchParams({ chunk: String(chunkIndex) });
  if (offset && offset > 0) params.set("offset", String(offset));
  return `/read/${bookId}?${params.toString()}`;
}

function errMessage(err: unknown): string {
  if (err instanceof LumiApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

export default function SavedPage() {
  const api = useMemo(() => createLumiApiClient(), []);
  const [tab, setTab] = useState<Tab>("bookmarks");
  const [data, setData] = useState<BookAnnotations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const books = await api.listBooks();
      const annotated = await Promise.all(
        books.items.map(async (book) => {
          const [bookmarks, highlights] = await Promise.all([
            listBookmarks(book.id),
            listHighlights(book.id),
          ]);
          return {
            bookId: book.id,
            title: book.title,
            bookmarks,
            highlights,
          } satisfies BookAnnotations;
        }),
      );
      setData(annotated);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(() => {
    let bookmarks = 0;
    let highlights = 0;
    for (const b of data) {
      bookmarks += b.bookmarks.length;
      highlights += b.highlights.length;
    }
    return { bookmarks, highlights };
  }, [data]);

  // Only books that have at least one annotation of the active type.
  const visible = useMemo(
    () =>
      data.filter((b) =>
        tab === "bookmarks" ? b.bookmarks.length > 0 : b.highlights.length > 0,
      ),
    [data, tab],
  );

  const isEmpty =
    tab === "bookmarks" ? totals.bookmarks === 0 : totals.highlights === 0;

  return (
    <div className="flex min-h-screen flex-col bg-app-bg pl-16 md:pl-56">
      <NavBar />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 md:px-8">
        <h1 className="text-4xl font-semibold text-app-text">Saved</h1>
        <p className="mt-2 text-app-body">
          Your bookmarks and highlights from across your library.
        </p>

        {/* Tabs */}
        <div className="mt-6 flex gap-2" role="tablist" aria-label="Annotation type">
          {(
            [
              ["bookmarks", "Bookmarks", totals.bookmarks],
              ["highlights", "Highlights", totals.highlights],
            ] as [Tab, string, number][]
          ).map(([value, label, count]) => {
            const active = tab === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(value)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-app-text text-app-bg"
                    : "bg-app-tinted text-app-body hover:bg-app-tinted"
                }`}
              >
                {label}
                {count > 0 ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>

        {/* States */}
        {loading ? (
          <div className="mt-16 flex items-center justify-center gap-2 text-app-body">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading your saved items…
          </div>
        ) : error ? (
          <div className="mt-8 rounded-md border border-app-border bg-app-tinted px-4 py-3 text-sm text-app-body">
            {error}{" "}
            <button
              type="button"
              onClick={() => void load()}
              className="font-semibold text-app-text underline"
            >
              Retry
            </button>
          </div>
        ) : isEmpty ? (
          <div className="mt-16 text-center text-app-body">
            <p className="text-lg font-medium text-app-text">
              No {tab} yet
            </p>
            <p className="mt-1 text-sm">
              Open a book in the reader and {tab === "bookmarks" ? "bookmark" : "highlight"}{" "}
              a passage to see it here.
            </p>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            {visible.map((book) => (
              <section key={book.bookId}>
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="truncate text-lg font-semibold text-app-text">
                    {book.title}
                  </h2>
                  <Link
                    href={`/read/${book.bookId}`}
                    className="shrink-0 text-xs font-medium text-app-teal hover:underline"
                  >
                    Open book
                  </Link>
                </div>

                <ul className="divide-y divide-app-border overflow-hidden rounded-md border border-app-border">
                  {tab === "bookmarks"
                    ? [...book.bookmarks]
                        .sort(
                          (a, b) =>
                            a.chunk_index - b.chunk_index ||
                            a.char_offset - b.char_offset,
                        )
                        .map((bm) => (
                          <li key={bm.id}>
                            <Link
                              href={readerHref(book.bookId, bm.chunk_index, bm.char_offset)}
                              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-app-tinted"
                            >
                              <BookmarkIcon className="h-4 w-4 shrink-0 text-app-body" />
                              <div className="min-w-0">
                                <div className="truncate font-medium text-app-text">
                                  {bm.label?.trim() ||
                                    locLabel(bm.chunk_index, bm.char_offset)}
                                </div>
                                {bm.label?.trim() ? (
                                  <div className="truncate text-xs text-app-body">
                                    {locLabel(bm.chunk_index, bm.char_offset)}
                                  </div>
                                ) : null}
                              </div>
                            </Link>
                          </li>
                        ))
                    : [...book.highlights]
                        .sort(
                          (a, b) =>
                            a.chunk_index - b.chunk_index ||
                            a.start_offset - b.start_offset,
                        )
                        .map((hl) => (
                          <li key={hl.id}>
                            <Link
                              href={readerHref(book.bookId, hl.chunk_index, hl.start_offset)}
                              className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-app-tinted"
                            >
                              <span
                                aria-hidden
                                className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
                                style={{ background: hl.color?.trim() || "#C9ADA7" }}
                              />
                              <div className="min-w-0">
                                <div className="text-xs text-app-body">
                                  {locLabel(hl.chunk_index, hl.start_offset)} · chars{" "}
                                  {hl.start_offset}–{hl.end_offset}
                                </div>
                                {hl.note?.trim() ? (
                                  <div className="mt-0.5 line-clamp-3 italic text-app-text">
                                    “{hl.note.trim()}”
                                  </div>
                                ) : null}
                              </div>
                            </Link>
                          </li>
                        ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
